import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';

export interface NumericInputV2Props extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> {
  value?: number | string | null;
  onValueChange?: (value: number) => void;
}

const toNumericValue = (value: number | string | null | undefined) => {
  if (value === '' || value === null || value === undefined) return 0;

  const numericValue = Number(String(value).replace(',', '.'));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export function NumericInputV2({ value, onValueChange, ...inputProps }: NumericInputV2Props) {
  const [displayValue, setDisplayValue] = useState(() => (value === null || value === undefined ? '' : String(value)));
  const lastEmittedValueRef = useRef(toNumericValue(value));

  useEffect(() => {
    const nextValue = toNumericValue(value);
    if (nextValue === lastEmittedValueRef.current) return;

    lastEmittedValueRef.current = nextValue;
    setDisplayValue(value === null || value === undefined ? '' : String(value));
  }, [value]);

  return (
    <input
      {...inputProps}
      type='text'
      inputMode='decimal'
      value={displayValue}
      onChange={event => {
        const nextDisplayValue = event.target.value;
        if (!/^\d*(?:[.,]\d*)?$/.test(nextDisplayValue)) return;

        const nextValue = toNumericValue(nextDisplayValue);
        lastEmittedValueRef.current = nextValue;
        setDisplayValue(nextDisplayValue);
        onValueChange?.(nextValue);
      }}
    />
  );
}
